import { useRef, useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { TextField } from 'components'
import { KeyType } from 'enums'

function toHoursAndMinutes(interval) {
  const hours = Math.floor(interval / 3600) || 0
  const minutes = Math.floor((interval % 3600) / (60)) || 0
  return [hours, minutes]
}

function KeySection(props, ref) {
  const { store, org } = props
  const { t } = useTranslation(null, { useSuspense: false })

  const bleCheckbox = useRef()
  const ephemeralCheckbox = useRef()
  const expirationHrs = useRef()
  const expirationMins = useRef()

  // if org has localKeyExpirationAllowed == false, then we should maybe just display the org's
  // values, and have the fields be disabled, with an explanation
  const allowOverride = org?.localKeyExpirationAllowed

  // if the org has localKeyExpirationAllowed, display a checkbox of whether or not the store wants to override the default values
  const [override, setOverride] = useState(allowOverride && store?.keyExpTimeIntervalEnabled != null)
  const shouldKeysExpireCB = useRef()
  const [shouldKeysExpire, setShouldKeysExpire] = useState(override ? store.keyExpTimeIntervalEnabled : org?.keyExpTimeIntervalEnabled)

  // if it's not checked, then display the org's values in the fields, and have them be disabled
  // if the store does want to override, enable the fields and if the store has values for the fields, set them to the store's values

  const defaultInterval = override ? store?.keyExpTimeInterval : org?.keyExpTimeInterval
  const [defaultHours, defaultMinutes] = toHoursAndMinutes(defaultInterval)

  const calcInterval = () => {
    if (!override || !shouldKeysExpire) return null
    const hours = Number(expirationHrs?.current?.value) || 0
    const minutes = Number(expirationMins?.current?.value) || 0
    const intervalEnabled = shouldKeysExpire && !(hours == 0 && minutes == 0)
    const interval = intervalEnabled ? ((hours * 60) + minutes) * 60 : null
    return interval
  }

  useImperativeHandle(ref, ()=>({
    get allowedKeyTypes() {
      const keyTypes = [KeyType.rfid]
      if (bleCheckbox?.current?.checked) keyTypes.push(KeyType.ble)
      if (ephemeralCheckbox?.current?.checked) keyTypes.push(KeyType.ephemeral)
      return keyTypes
    },
    get expirationEnabled() {
      return override ? !!(shouldKeysExpire && calcInterval()) : null
    },
    get expirationInterval() {
      return calcInterval()
    },
  }))

  const onOverrideChanged = event => {
    const v = event.target.checked
    setOverride(v)
    if (v) return
    // if they disable the override, change the values back to the org values
    const [h,m] = toHoursAndMinutes(org?.keyExpTimeInterval)
    expirationHrs.current.value = h
    expirationMins.current.value = m
    shouldKeysExpireCB.current.checked = org?.keyExpTimeIntervalEnabled
    setShouldKeysExpire(org?.keyExpTimeIntervalEnabled)
  }

  // If the ephemeral checkbox is toggled on, make sure to enable BLE as well
  const onEphemeralToggled = () => {
    if (ephemeralCheckbox?.current?.checked && bleCheckbox?.current)
      bleCheckbox.current.checked = true
  }

  // If BLE is toggled off, make sure to disable ephemeral as well
  const onBLEToggled = () => {
    if (!bleCheckbox?.current?.checked && ephemeralCheckbox?.current)
      ephemeralCheckbox.current.checked = false
  }

  return (
    <div className='section col'>
      <p className='subheading'>{t('resource.key', {count: 100})}</p>
      <label htmlFor='key-types' className='subtitle'>{t('organizationDetails.keyAllowedTypes')} *</label>
      <div className='col'>
        <Checkbox label={t('key.type.rfid')} checked disabled />
        <Checkbox label={t('key.type.ble')} ref={bleCheckbox} defaultChecked={store?.allowedKeyTypes.includes(KeyType.ble)} onChange={onBLEToggled} />
        <Checkbox label={t('key.type.ephemeral')} ref={ephemeralCheckbox} defaultChecked={store?.allowedKeyTypes.includes(KeyType.ephemeral)} onChange={onEphemeralToggled} />
        <p className='subtitle'>{t('key.ephemeralWarning')}</p>
      </div>

      {allowOverride && <Checkbox label={t('storeDetails.overrideKeyExpiration')} defaultChecked={override} onChange={onOverrideChanged} />}
      <Checkbox
        label={t('organizationDetails.keyExpiration')}
        ref={shouldKeysExpireCB}
        defaultChecked={shouldKeysExpire}
        disabled={!override}
        onChange={event=>setShouldKeysExpire(event.target.checked)} />

      <div className={`row key-exp-container ${!shouldKeysExpire ? 'hidden' : ''}`}>
        <TextField
          label={t('organizationEdit.enterHours')}
          ref={expirationHrs}
          defaultValue={defaultHours}
          disabled={!override}
          type='number'
          min='0' />
        <TextField
          label={t('organizationEdit.enterMinutes')}
          ref={expirationMins}
          defaultValue={defaultMinutes}
          disabled={!override}
          type='number'
          min='0' />
      </div>
    </div>
  )
}

export default forwardRef(KeySection)

function CB(props, ref) {
  const { label, ...otherProps } = props
  const id = label.replaceAll(/[^A-Za-z0-9-_:.]/g, '-')
  return (
    <div className='row'>
      <input id={id} type='checkbox' { ...otherProps } ref={ref} />
      <label htmlFor={id}>{label}</label>
    </div>
  )
}
const Checkbox = forwardRef(CB)
